import React, { useState, useEffect, useCallback } from 'react';
import accountService from '../services/accountService';

// SyncConnect client build settings for a chain.
//
// Everything the MSI used to ask the on-site user is set here instead, so the generated client for any
// sync project under this chain is fully configured and its installer is next-next-finish. The project
// itself contributes only ProjectId and the hconnect endpoint — both known at build time.
//
// Superadmin only: this holds the Hilton tokenisation credentials.
//
// PRESENTATIONAL ONLY. The settings live in their own table behind their own endpoint, but on screen they
// are just another tab of the chain, so AccountForm owns the state and its "Update Hotel Chain" button
// saves them along with everything else. This component renders fields and reports changes upward — it has
// no save button of its own, because a second save button on a form that already has one is a trap.
//
// Uses the surrounding screen's form-section/form-row/form-group markup rather than the newer acp-*
// classes — a modern-classed island inside AccountForm's legacy layout would not line up with the tabs
// either side of it.

const LOG_LEVELS = [
  { value: '0', label: '0 - Error only' },
  { value: '1', label: '1 - Warnings and errors' },
  { value: '2', label: '2 - Info (recommended)' },
  { value: '3', label: '3 - Debug (troubleshooting)' },
  { value: '4', label: '4 - Trace (maximum verbosity)' },
];

// Sent when a secret field is left as the mask — the API treats it as "leave unchanged". Clearing one is
// the explicit CLEAR sentinel, so a save can never silently wipe a credential it was not shown.
export const MASK = '********';

export const SECRET_FIELDS = ['htsApiKey', 'htsAppIdentifier', 'htsUatApiKey', 'htsUatAppIdentifier'];

// An untouched secret still holds the mask — drop it so the server keeps the stored value. Exported
// because the save now happens in AccountForm, which must not ship the mask back as a credential.
export const stripMaskedSecrets = (settings) => {
  const payload = { ...settings };
  SECRET_FIELDS.forEach(field => { if (payload[field] === MASK) delete payload[field]; });
  return payload;
};

const AccountSyncConnectTab = ({ accountId, isViewMode, value, onLoad, onChange }) => {
  const [loading, setLoading] = useState(!value);
  const [error, setError] = useState(null);
  const [revealed, setRevealed] = useState(false);

  // Loaded once, the first time the tab is opened; after that the parent holds it, so switching tabs and
  // coming back keeps any unsaved edits.
  const load = useCallback(async (reveal = false) => {
    setLoading(true);
    try {
      const data = await accountService.getSyncConnectSettings(accountId, reveal);
      onLoad(data.settings || {}, reveal);
      setRevealed(reveal);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load SyncConnect settings');
    } finally {
      setLoading(false);
    }
  }, [accountId, onLoad]);

  useEffect(() => { if (accountId && !value) load(false); }, [accountId, value, load]);

  const form = value || {};
  const set = (field, fieldValue) => onChange({ ...form, [field]: fieldValue });

  if (loading) return <div className="loading">Loading SyncConnect settings...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!value) return <div className="no-data-hint" style={{ padding: '1rem 0' }}>No SyncConnect settings available.</div>;

  const isHilton = String(form.clientMode).toUpperCase() === 'HILTON';
  const usesProxy = String(form.useProxy).toUpperCase() === 'Y';

  const secretField = (id, label, hint) => (
    <div className="form-group">
      <label htmlFor={id}>{label}</label>
      <input
        type={revealed ? 'text' : 'password'}
        id={id}
        name={id}
        value={form[id] || ''}
        onChange={(e) => set(id, e.target.value)}
        disabled={isViewMode}
        autoComplete="new-password"
        placeholder={form[`${id}Set`] ? 'Configured' : 'Not set'}
      />
      {hint && <small className="field-hint">{hint}</small>}
    </div>
  );

  return (
    <div className="tab-panel">
      <div className="no-data-hint" style={{ padding: '0 0 1rem' }}>
        These values are baked into every SyncConnect client generated for a sync project in this chain.
        The Project ID and the hconnect endpoint come from the project itself, so the installer asks the
        on-site user nothing.
      </div>

      <div className="form-section">
        <h3>Client Mode</h3>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="clientMode">Mode</label>
            <select
              id="clientMode"
              value={form.clientMode || 'NONE'}
              onChange={(e) => set('clientMode', e.target.value)}
              disabled={isViewMode}
            >
              <option value="NONE">Database sync only</option>
              <option value="HILTON">HILTON - OXI proxy + tokenisation</option>
            </select>
            <small className="field-hint">
              The /health endpoint is served in both modes; HILTON additionally serves the OXI proxy routes.
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="listenerPort">Listener Port</label>
            <input
              type="text"
              id="listenerPort"
              value={form.listenerPort || ''}
              onChange={(e) => set('listenerPort', e.target.value)}
              disabled={isViewMode}
              placeholder="8081"
            />
            <small className="field-hint">The web server runs in every mode, so a port is always required.</small>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Network</h3>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="useProxy">Use Corporate Proxy</label>
            <select
              id="useProxy"
              value={usesProxy ? 'Y' : 'N'}
              onChange={(e) => set('useProxy', e.target.value)}
              disabled={isViewMode}
            >
              <option value="N">No</option>
              <option value="Y">Yes</option>
            </select>
          </div>

          {usesProxy && (
            <div className="form-group">
              <label htmlFor="proxyUrl">Proxy URL</label>
              <input
                type="text"
                id="proxyUrl"
                value={form.proxyUrl || ''}
                onChange={(e) => set('proxyUrl', e.target.value)}
                disabled={isViewMode}
                placeholder="http://proxy.example.com:3128"
              />
            </div>
          )}
        </div>
      </div>

      <div className="form-section">
        <h3>Logging</h3>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="logLevel">Log Level</label>
            <select
              id="logLevel"
              value={String(form.logLevel != null ? form.logLevel : 2)}
              onChange={(e) => set('logLevel', e.target.value)}
              disabled={isViewMode}
            >
              {LOG_LEVELS.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
            </select>
            <small className="field-hint">No personally identifiable information is logged at any level.</small>
          </div>

          <div className="form-group">
            <label htmlFor="debugMode">Debug Mode</label>
            <select
              id="debugMode"
              value={String(form.debugMode).toUpperCase() === 'Y' ? 'Y' : 'N'}
              onChange={(e) => set('debugMode', e.target.value)}
              disabled={isViewMode}
            >
              <option value="N">No</option>
              <option value="Y">Yes</option>
            </select>
            <small className="field-hint">Additional HTTP-level tracing on top of the log level.</small>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h3>Install</h3>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="installFolder">Install Folder</label>
            <input
              type="text"
              id="installFolder"
              value={form.installFolder || ''}
              onChange={(e) => set('installFolder', e.target.value)}
              disabled={isViewMode}
              placeholder="C:\\Program Files\\hconnect\\syncConnect"
            />
            <small className="field-hint">Leave blank for the installer default.</small>
          </div>
        </div>
      </div>

      <div className="form-section">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h3>Tokenisation Service (HTS)</h3>
          {!isViewMode && (
            <button
              type="button"
              className="btn-secondary sa-only"
              onClick={() => load(!revealed)}
            >
              {revealed ? 'Hide secrets' : 'Show secrets'}
            </button>
          )}
        </div>
        {!isHilton && (
          <div className="no-data-hint" style={{ padding: '0 0 0.75rem' }}>
            Only used in HILTON mode — the client reads these solely from the OXI tokenisation proxy.
          </div>
        )}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="htsUrl">HTS URL (Production)</label>
            <input
              type="text"
              id="htsUrl"
              value={form.htsUrl || ''}
              onChange={(e) => set('htsUrl', e.target.value)}
              disabled={isViewMode}
              placeholder="https://hwts.hilton.com:9900/hts/tokenize"
            />
          </div>
          {secretField('htsApiKey', 'HTS API Key (Production)')}
        </div>
        <div className="form-row">
          {secretField('htsAppIdentifier', 'HTS App Identifier (Production)')}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="htsUatUrl">HTS URL (UAT)</label>
            <input
              type="text"
              id="htsUatUrl"
              value={form.htsUatUrl || ''}
              onChange={(e) => set('htsUatUrl', e.target.value)}
              disabled={isViewMode}
              placeholder="https://hwts-uat.hilton.com:9900/hts/tokenize"
            />
          </div>
          {secretField('htsUatApiKey', 'HTS API Key (UAT)')}
        </div>
        <div className="form-row">
          {secretField('htsUatAppIdentifier', 'HTS App Identifier (UAT)',
            'Production clients use the production values; every lower environment is built against UAT.')}
        </div>
      </div>
    </div>
  );
};

export default AccountSyncConnectTab;
